import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { type PriceFormValues } from '@eduzz-product-manager/validators';

import { SimpleDivider } from '@/components/ui/SimpleDivider';
import { DEFAULT_MAXIMUM_LATE_PAYMENTS } from '@/env';
import { useZustandSync } from '@/hooks/useZustandSync';
import { type BillingCycleType } from '@/stores/productStore';

import { BillingCycle } from './BillingCycle';
import { Conditions } from './Conditions';
import { MaximumLatePayments } from './MaximumLatePayments';
import { Price } from './Price';
import { RecurrenceCount } from './RecurrenceCount';

const billingCycleToIntervalMap: Record<BillingCycleType, { type: 'months' | 'years'; value: number }> = {
  one_month: {
    type: 'months',
    value: 1
  },
  three_months: {
    type: 'months',
    value: 3
  },
  six_months: {
    type: 'months',
    value: 6
  },
  twelve_months: {
    type: 'years',
    value: 1
  },
  custom: {
    type: 'months',
    value: 1
  }
};

type CustomPriceFormValues = PriceFormValues & {
  subscriptionBillingCycleType: 'months' | 'years';
  subscriptionBillingCycleQuantity: number;
};

export const Subscription = () => {
  const form = useFormContext<CustomPriceFormValues>();

  const billingCycle = useWatch({ control: form.control, name: 'billingCycle' });

  useEffect(() => {
    if (!billingCycle || billingCycle == 'custom') {
      return;
    }

    form.setValue('subscriptionBillingCycleQuantity', billingCycleToIntervalMap[billingCycle].value);
    form.setValue('subscriptionBillingCycleType', billingCycleToIntervalMap[billingCycle].type);
  }, [billingCycle, form]);

  useZustandSync<CustomPriceFormValues>(form.control, (data, product) => ({
    price: data.price,
    payment: {
      ...product.payment,
      billingCycle: data.billingCycle!,
      billingConfig: {
        type: data.subscriptionBillingCycleType,
        value: Number(data.subscriptionBillingCycleQuantity)
      },
      recurrenceCount: Number(data.recurrenceCount ?? 0),
      maximumLatePayments: Number(data.maximumLatePayments ?? DEFAULT_MAXIMUM_LATE_PAYMENTS),
      activationFeeValue: data.activationFeeValue,
      trialDays: data.trialDays
    }
  }));

  return (
    <div>
      <Price />
      <SimpleDivider />
      <BillingCycle />
      <SimpleDivider />
      <RecurrenceCount />
      <SimpleDivider />
      <MaximumLatePayments />
      <SimpleDivider />
      <Conditions />
    </div>
  );
};
